import { Button } from "../components/Button";
import { joinRoulette, getRouletteActiveCount } from "~/api/roomApi";
import { useContext, useState, useEffect } from "react";
import { AuthContext } from "~/utils/useAuth";
import { useSnapshot } from "valtio";
import { localState } from "~/state/state";

import { SimpleLoadingIndicator } from "~/pages/loading/LoadingPage";
import { GUIDELINES_LAST_UPDATED, ROULETTE_GUIDELINES_TEXT } from "~/assets/guidelines";
import { GuidelinesScrollContainer } from "../components/GuidelinesScrollContainer";
import { sendFriendlyToast } from "~/utils/handleToasts";
import { keyframes, styled } from "~/ui/style/stitches.config";
import { trackEvent } from "~/api/analyticsApi";
import { imageUrl } from "~/utils/imageurl";


const simpleOrbit = keyframes({
  "0%": { offsetDistance: "0%" },
  "100%": { offsetDistance: "100%" },
});

const simpleOrbit2 = keyframes({
  "0%": { offsetDistance: "50%" },
  "100%": { offsetDistance: "150%" },
});

const simpleOrbitReverse = keyframes({
  "0%": { offsetDistance: "0%" },
  "100%": { offsetDistance: "-100%" },
});

const simpleOrbitReverse2 = keyframes({
  "0%": { offsetDistance: "50%" },
  "100%": { offsetDistance: "-50%" },
});

// Curated list of animals for roulette announcement
const ROULETTE_ANIMALS = [
  "owl.png", 
  "rabbit.png",
  "frog.png",
  "duck.png",
  "gecko.png",
  "crow.png",
  "fox.png",
  "sheep.png",
  "koala.png",
  "jellyfish.png",
  "crocodile.png",
];

// Curated list of Gothmancy cards for roulette announcement
const ROULETTE_GOTHMANCY_CARDS = [
  "00-the-fool.png",
  "01-the-magician.png",
  "02-the-high-priestess.png",
  "03-the-empress.png",
  "08-strength.png",
  "17-the-star.png",
  "19-the-sun.png",
  "21-the-world.png"
];

const getRandomRouletteAnimal = () => {
  return ROULETTE_ANIMALS[Math.floor(Math.random() * ROULETTE_ANIMALS.length)];
};

const getRandomGothmancyCard = () => {
  return ROULETTE_GOTHMANCY_CARDS[Math.floor(Math.random() * ROULETTE_GOTHMANCY_CARDS.length)];
};

const getTwoRandomRouletteAnimals = () => {
  const first = getRandomRouletteAnimal();
  let second = getRandomRouletteAnimal();
  
  // Ensure they're different
  while (second === first) {
    second = getRandomRouletteAnimal();
  }
  
  return [first, second];
};

export const RouletteAnnouncement = ({ shouldShowRoulette }: { shouldShowRoulette: boolean }) => {
  const { user, updateUser } = useContext(AuthContext);
  const { appDarkMode } = useSnapshot(localState);
  const [activeRouletteUsers, setActiveRouletteUsers] = useState(0);
  const [isJoining, setIsJoining] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [showTips, setShowTips] = useState(false);
  const [isHoveringButton, setIsHoveringButton] = useState(false);
  const [randomProfilePics, setRandomProfilePics] = useState<string[]>([]);

  useEffect(() => {
    // Get two different random animals from curated list
    setRandomProfilePics(getTwoRandomRouletteAnimals());
  }, []);

  const hasAcceptedGuidelines = user?.acceptedTerms === GUIDELINES_LAST_UPDATED;

  const handleJoinRoulette = async () => {
    if (isJoining) return;
    
    trackEvent("/home/roulette/pressed_join");
    
    setIsJoining(true);
    
    // Always show guidelines for roulette, regardless of login status
    setShowGuidelines(true);
    setIsJoining(false);
  };

  const handleGuidelinesAccept = () => {
    trackEvent("/home/roulette/accepted_guidelines");
    
    // Only update user's accepted terms if they're logged in and haven't agreed before
    if (user && !hasAcceptedGuidelines) {
      updateUser({ acceptedTerms: GUIDELINES_LAST_UPDATED }).then(() => {
        sendFriendlyToast(1152);
        setShowGuidelines(false);
        setShowTips(true);
      });
    } else {
      // Either already accepted or not logged in - just proceed
      setShowGuidelines(false);
      setShowTips(true);
    }
  };

  const handleTipsComplete = () => {
    trackEvent("/home/roulette/ready_to_match");
    
    setShowTips(false);
    
    if (!user) {
      // Not logged in, go to signup with roulette destination
      window.location.href = `/signup?dest=/roulette`;
    } else {
      // Already logged in, proceed directly to roulette
      proceedWithRoulette();
    }
  };

  const proceedWithRoulette = async () => {
    setIsJoining(true);
    try {
      const room = await joinRoulette();
      window.location.href = `/room/${room.name}`;
    } catch (error) {
      console.error('Roulette failed:', error);
      setIsJoining(false);
    }
  };

  useEffect(() => {
    const fetchRouletteCount = async () => {
      const count = await getRouletteActiveCount();
      setActiveRouletteUsers(count);
    };

    fetchRouletteCount();
    const interval = setInterval(fetchRouletteCount, 45000);

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {/* Guidelines Modal */}
      {showGuidelines && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: appDarkMode
              ? "rgba(0, 0, 0, 0.7)"
              : "rgba(133, 133, 133, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 100000,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowGuidelines(false);
            }
          }}
        >
          <StyledGuidelinesModal>
            <img
              src="/images/exit.svg"
              style={{
                position: "absolute",
                top: "20px",
                right: "20px",
                width: "28px",
                height: "28px",
                cursor: "pointer",
                opacity: 0.6,
                transition: "opacity 0.2s ease",
              }}
              onMouseOver={e => e.currentTarget.style.opacity = '1'}
              onMouseOut={e => e.currentTarget.style.opacity = '0.6'}
              onClick={() => setShowGuidelines(false)}
            />
            <StyledModalText>
              Please read and agree to our community guidelines.
            </StyledModalText>
            <GuidelinesScrollContainer 
              onClickAgree={handleGuidelinesAccept}
              guidelinesText={ROULETTE_GUIDELINES_TEXT}
              style={{ height: "calc(100% - 100px)" }}
            />
          </StyledGuidelinesModal>
        </div>
      )}

      {/* Tips Modal */}
      {showTips && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: appDarkMode
              ? "rgba(0, 0, 0, 0.7)"
              : "rgba(133, 133, 133, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 100000,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowTips(false);
            }
          }}
        >
          <StyledGuidelinesModal>
            <img
              src="/images/exit.svg"
              style={{
                position: "absolute",
                top: "20px",
                right: "20px",
                width: "28px",
                height: "28px",
                cursor: "pointer",
                opacity: 0.6,
                transition: "opacity 0.2s ease",
              }}
              onMouseOver={e => e.currentTarget.style.opacity = '1'}
              onMouseOut={e => e.currentTarget.style.opacity = '0.6'}
              onClick={() => setShowTips(false)}
            />
            <StyledModalText style={{ fontFamily: "KeplerLightCondensed", fontSize: "26px", textAlign: "left" }}>
              Quick tips before you join.
            </StyledModalText>
            <div style={{ 
              flex: 1, 
              overflowY: "auto", 
              padding: "20px 0",
              fontSize: "13px",
              lineHeight: "1.5"
            }}>
              <StyledTipItem>
                <StyledTipTitle>Enable audio permissions.</StyledTipTitle>
                <div>Video is optional. Voice-only is totally fine.</div>
              </StyledTipItem>
              <StyledTipItem>
                <StyledTipTitle>Take turns reading.</StyledTipTitle>
                <div>One person reads, then switch. It's collaborative.</div>
              </StyledTipItem>
              <StyledTipItem>
                <StyledTipTitle>Guidebooks are there if you need them.</StyledTipTitle>
                <div>Or just learn from each other! There are many ways to read.</div>
              </StyledTipItem>
              <StyledTipItem>
                <StyledTipTitle>You can leave anytime.</StyledTipTitle>
                <div>It's okay to politely end the session.</div>
              </StyledTipItem>
              <div style={{
                fontSize: "11px",
                lineHeight: "1.5",
                background: "#e5e5e5",
                borderRadius: "12px",
                padding: "14px 20px 14px 20px",
                marginTop: "20px",
              }}>
                This is a new feature! You might have to wait in the room or check back later, depending on how many people are online.</div>
            </div>
            <Button
              onClick={handleTipsComplete}
              style={{
                fontSize: "13px",
                padding: "10px 20px",
                borderRadius: "18px",
                background: "black",
                color: "white",
                fontWeight: "400",
                transition: "all 0.2s",
                cursor: "pointer",
                width: "160px",
                height: "52px",
                margin: "10px auto 0",
              }}
              css={{
                "@mobile": {
                  position: "absolute",
                  bottom: "60px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  margin: 0,
                }
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#333";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "black";
              }}
            >
              I'm ready!
            </Button>
          </StyledGuidelinesModal>
        </div>
      )}

      {/* Existing announcement content */}
      <StyledRouletteContainer
        data-roulette-announcement
        style={{
          display: shouldShowRoulette ? "flex" : "none",
        }}
      >
        <div
          style={{
            backgroundColor: "#f4f4f4",
            backgroundImage: `
              linear-gradient(rgba(0,0,0,0.06) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,0,0,0.06) 1px, transparent 1px)
            `,
            backgroundSize: "12px 12px",
            color: "#333",
            border: "0.5px solid rgba(0, 0, 0, 0.4)",
            borderRadius: "31px",
            boxShadow: "0px 3px 0px 0px rgba(0, 0, 0, 1)",
            padding: "40px 60px",
            fontSize: "12px",
            textAlign: "center",
            lineHeight: "1.3",
            width: "600px",
            maxWidth: "calc(100% - 32px)",
            marginBottom: "-20px",
          }}
        >
          <div style={{ marginBottom: "8px" }}>
            {/* Animal profile pictures */}
            <div style={{
              display: "flex",
              justifyContent: "center",
              gap: "8px",
              marginBottom: "20px",
            }}>
              <img
                src={imageUrl(`/profile-pics/${randomProfilePics[0]}`, 160)}
                style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  objectFit: "cover"
                }}
              />
              <img
                src={imageUrl(`/profile-pics/${randomProfilePics[1]}`, 160)}
                style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  objectFit: "cover"
                }}
              />
            </div>
            
            <StyledTitle>
              Want a tarot practice buddy?
            </StyledTitle>
            <div style={{
              fontSize: "14px",
              marginBottom: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "500",
              gap: "6px"
            }}>
              Try WitchRoulette!
              <span style={{
                fontSize: "9px",
                backgroundColor: "#ddd",
                color: "#666",
                padding: "2px 6px",
                borderRadius: "8px",
                fontWeight: "400",
                letterSpacing: "0.02em",
                textTransform: "uppercase",
              }}>
                free beta
              </span>
            </div>
            <StyledDescriptionText>
              Match with a random person in the Moonlight community. Join a shared tarot room and read for each other. Open to all levels!
            </StyledDescriptionText>
          </div>

          {/* Oval button */}
          <StyledOvalButton
            data-roulette-button
            onClick={handleJoinRoulette}
            onMouseEnter={() => setIsHoveringButton(true)}
            onMouseLeave={() => setIsHoveringButton(false)}
            isHovering={isHoveringButton}
          >
            <StyledOvalBorder isHovering={isHoveringButton} />
            
            {/* Rotating planets container */}
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: "0px",
                height: "0px",
                transform: "translate(-50%, -50%) rotate(-12deg)",
              }}
            >
              {/* Planet 1 */}
              <div
                style={{
                  position: "absolute",
                  width: "6px",
                  height: "6px",
                  backgroundColor: "#333",
                  borderRadius: "50%",
                  offsetPath: "ellipse(90px 35px at center)",
                  animation: `${simpleOrbit} 4s linear infinite`,
                  transform: isHoveringButton ? "scale(1.3)" : "scale(1)",
                  transition: "transform 0.2s ease",
                }}
              />
              {/* Planet 2 */}
              <div
                style={{
                  position: "absolute",
                  width: "6px",
                  height: "6px",
                  backgroundColor: "#333",
                  borderRadius: "50%",
                  offsetPath: "ellipse(90px 35px at center)",
                  animation: `${simpleOrbit2} 4s linear infinite`,
                  transform: isHoveringButton ? "scale(1.3)" : "scale(1)",
                  transition: "transform 0.2s ease",
                }}
              />
            </div>
            
            <StyledButtonText>
              {isJoining ? (
                <SimpleLoadingIndicator style={{ 
                  width: "15px", 
                  height: "15px",
                  position: "relative",
                  top: "0px",
                  opacity: "0.4"
                }} />
              ) : (
                "Find a match"
              )}
            </StyledButtonText>
          </StyledOvalButton>
          {activeRouletteUsers > 0 && (
            <div style={{
              marginTop: "8px",
              fontSize: "11px",
              textAlign: "center",
              color: "rgb(0, 0, 0, 0.7)",
              backgroundColor: "rgb(218, 238, 161)",
              padding: "3px 12px",
              borderRadius: "12px",
              display: "inline-block",
            }}>
              <strong style={{ color: "#000" }}>{activeRouletteUsers}</strong> {activeRouletteUsers === 1 ? "person waiting" : "people reading"}
            </div>
          )}
        </div>
      </StyledRouletteContainer>
    </>
  );
};



const StyledGuidelinesModal = styled("div", {
  backgroundColor: "#f6f6f6",
  border: "1px solid #000",
  borderRadius: "20px",
  padding: "40px",
  maxWidth: "500px",
  maxHeight: "80vh",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  
  "@mobile": {
    border: "none",
    borderRadius: "0",
    padding: "30px",
    maxWidth: "100%",
    maxHeight: "100%",
    width: "100%",
    height: "100%",
  }
});

const StyledModalText = styled("div", {
  fontSize: "13px",
  margin: "30px 0 5px 0",
  textAlign: "center",
  lineHeight: "1.4"
});

const StyledTipItem = styled("div", {
  marginBottom: "16px",
  "&:last-child": {
    marginBottom: 0,
  }
});

const StyledTipTitle = styled("div", {
  fontWeight: "600",
  marginBottom: "3px"
});

const StyledDescriptionText = styled("div", {
  fontSize: "12px",
  lineHeight: "1.4em",
  textAlign: "center",
  padding: "0px 0px",
  margin: "0 auto",
  maxWidth: "380px",
  
  "@mobile": {
    maxWidth: "none",
    margin: "0 -20px"
  }
});

const StyledTitle = styled("div", {
  fontFamily: "KeplerLightCondensed",
  fontSize: "24px",
  fontWeight: "600", 
  marginBottom: "8px",
  
  "@mobile": {
    fontSize: "26px"
  }
});

const StyledOvalButton = styled("div", {
  position: "relative",
  width: "180px",
  height: "70px",
  cursor: "pointer",
  margin: "20px auto",
  transition: "transform 0.2s ease",
  
  variants: {
    isHovering: {
      true: {
        transform: "scale(1.05)",
      },
      false: {
        transform: "scale(1)",
      }
    }
  }
});

const StyledOvalBorder = styled("div", {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background: "#fff",
  border: "1px solid #333",
  borderRadius: "50%",
  transform: "rotate(-12deg)",
  transition: "all 0.2s ease",
  
  variants: {
    isHovering: {
      true: {
        background: "#f3f3f3",
      },
      false: {
        background: "#fff",
      }
    }
  }
});

const StyledButtonText = styled("div", {
  position: "relative",
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "14px",
  fontStyle: "italic",
  color: "#333",
});

const StyledRouletteContainer = styled("div", {
  width: "100%",
  justifyContent: "center",
  marginTop: "10px",
  marginBottom: "150px",

  "@mobile": {
    marginBottom: "200px",
    paddingBottom: "50px",
  }
});
